<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { computed, ref, useSlots } from 'vue';

import AppSlideUpTransition from '../transitions/appSlideUpTransition/AppSlideUpTransition.vue';
import { KeyboardKeyCode } from '../../models';

const slots = useSlots();
const hasFooter = computed(() => !!slots['footer']);

interface AppFirstLevelPanelEmits {
  (eventName: 'close'): void;
}

const emit = defineEmits<AppFirstLevelPanelEmits>();

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));

const root = ref<HTMLDivElement>();

defineExpose({
  root,
});
</script>

<template>
  <AppSlideUpTransition>
    <div ref="root" class="app-first-level-panel">
      <div class="wrapper">
        <div class="header">
          <slot name="header" />
        </div>
        <div class="content">
          <slot name="content" />
        </div>
      </div>
      <div v-if="hasFooter" class="wrapper-footer">
        <div class="footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </AppSlideUpTransition>
</template>

<style scoped lang="scss">
@import '../../styles/_media.scss';

.app-first-level-panel {
  --app-first-level-panel-width: 1120px;
  --app-first-level-panel-width-small: 944px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  // NOTE: Using dvh fixes the problem of address bar rendering over content in mobile Safari.
  // vh is here as a fallback for Firefox and old browsers, which do not support dvh.
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  z-index: var(--panel-first-level-z-index);
  background-color: var(--color-off-white);

  .wrapper {
    margin: 0 auto;
    max-width: var(--app-first-level-panel-width);
    @include for-less-or-equal-than-1200-px {
      max-width: var(--app-first-level-panel-width-small);
    }
    position: relative;

    .header {
      position: sticky;
      top: 0;
      // NOTE: Swiper library internally sets z-index to 1 for its content.
      // Header must be above that to correctly work as sticky header.
      z-index: calc(var(--panel-first-level-z-index) + 1);
    }
  }

  .wrapper-footer {
    position: sticky;
    bottom: 0;
    background-color: var(--color-white);
    border-top: 1px solid var(--color-neutral-30);

    .footer {
      margin: 0 auto;
      max-width: var(--app-first-level-panel-width);
    }
  }
}
::-webkit-scrollbar-track {
  background: var(--color-off-white);
}
</style>
